import { Component, EventEmitter, OnInit, ViewChild } from '@angular/core';
import {
  NgForm,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { RequestService } from 'src/app/services/request.service';
import { catchError, map, switchMap } from 'rxjs/operators';
import { merge, of as observableOf, throwError } from 'rxjs';
import { ExternalParty, User } from 'src/app/model/user';
import { SnackService } from 'src/app/services/snack.service';
import { Role } from '../../../model/role';
import { EmployeeType } from '../../../model/employee-type';
import UserHelper from '../../../helpers/user-helper';
import { FileUploader } from 'ng2-file-upload';
import Asset from '../../../model/asset';
import { StringHelper } from 'src/app/helpers/string-helper';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { MAX_UPLOAD_SIZE } from 'src/globals/globals';
import { ModalService } from 'src/app/services/modal.service';
import { FileHelper } from '../../../helpers/file-helper';
import AssetHelper from '../../../helpers/asset-helper';

@Component({
  selector: 'edit-user',
  templateUrl: './edit-user.component.html',
  styleUrls: ['./edit-user.component.scss'],
  providers: [AssetHelper],
})
export class EditUserComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @ViewChild('userForm')
  public myForm: NgForm;
  public isLoadingObject = false;
  public isInitialised = false;
  public userForm: UntypedFormGroup;
  private reloadData: EventEmitter<any>;
  private user: User = new User();
  employeetypes = [];
  saving = false;
  currentUserIsAdmin = false;
  isImage = FileHelper.isImage;
  returnRoute = '/users';

  externalParties: string[];

  public uploader: FileUploader = new FileUploader({
    url: '/api/assets/upload',
  });

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private snackService: SnackService,
    private requestService: RequestService,
    private modalService: ModalService,
    private router: Router,
    private assetHelper: AssetHelper
  ) {
    super();
    this.externalParties = Object.keys(ExternalParty).filter((v) =>
      isNaN(Number(v))
    );
    this.externalParties.push('');
  }

  ngOnInit() {
    this.addSubscription(this.route.queryParamMap, (params: Params) => {
      if (params.params && params.params['returnRoute']) {
        this.returnRoute = params.params['returnRoute'];
        console.log('this.returnRoute is: ' + this.returnRoute);
      }
    });
    this.userForm = this.fb.group({});
    this.reloadData = new EventEmitter<any>();
    this.initObject();
    this.checkIsAdmin();
    for (let thing in EmployeeType) {
      if (isNaN(Number(thing))) {
        this.employeetypes.push(thing);
      }
    }
  }

  private buildForm() {
    this.userForm = this.fb.group({
      firstName: [
        this.user.firstName,
        [Validators.required, Validators.minLength(3)],
      ],
      lastName: [
        this.user.lastName,
        [Validators.required, Validators.minLength(3)],
      ],
      email: [
        this.user.email,
        [Validators.required, Validators.email, Validators.minLength(3)],
      ],
      party: [this.user.party],
    });

    this.isInitialised = true;
  }

  private initObject(): void {
    merge(this.route.params, this.reloadData)
      .pipe(
        switchMap((params) => {
          this.isLoadingObject = true;
          if (params['id'] != null) {
            return this.requestService.get(
              '/users/' + params['id'],
              null,
              null,
              null,
              null,
              null,
              null
            );
          } else {
            let user = new User();
            return observableOf(user);
          }
        }),
        map((e: any) => {
          return e;
        }),
        catchError((e) => {
          this.isLoadingObject = false;
          if (UserHelper.isAdmin(this.user)) {
            this.router.navigateByUrl(this.returnRoute);
          } else if (!UserHelper.isClient(this.user)) {
            this.router.navigateByUrl('/projecten');
          } else {
            this.router.navigateByUrl('/client-corner/lopende');
          }

          return observableOf([]);
        })
      )
      .subscribe((data) => {
        this.user = data.body as User;
        if (this.user != null) {
          this.buildForm();
        } else {
          this.user = new User();
          this.buildForm();
        }
        // Flip flag to show that loading has finished.
        this.isLoadingObject = false;
      });
  }

  private checkIsAdmin() {
    this.authService
      .getCurrentUser()
      .subscribe(
        (res) => (this.currentUserIsAdmin = UserHelper.isAdmin(res.body))
      );
  }

  save() {
    if (this.userForm.valid) {
      this.user.firstName = this.userForm.get('firstName').value;
      this.user.lastName = this.userForm.get('lastName').value;
      this.user.email = this.userForm.get('email').value;
      this.user.party = this.userForm.get('party').value;
      this.saving = true;

      // NEW
      if (this.user.id === 0 || !this.user.id) {
        this.requestService.post(this.user, '/users').subscribe(
          (res) => {
            if (res) {
              if (res.success == false) {
                this.snackService.showError(res.message);
              } else {
                if (
                  UserHelper.isAdmin(this.authService.getAuthenticatedUser())
                ) {
                  this.router.navigateByUrl(this.returnRoute);
                } else if (
                  !UserHelper.isClient(this.authService.getAuthenticatedUser())
                ) {
                  this.router.navigateByUrl('/projecten');
                } else {
                  this.router.navigateByUrl('/client-corner/lopende');
                }
              }
            }
            this.saving = false;
          },
          (err) => {
            this.handleSaveError(err);
            this.saving = false;
          }
        );
      } // UPDATE
      else {
        this.requestService.put(this.user, '/users').subscribe(
          (res) => {
            if (res) {
              if (res.success == false) {
                this.snackService.showError(res.message);
              } else {
                this.snackService.showSuccess('Gebruiker succesvol aangepast');
                if (
                  UserHelper.isAdmin(this.authService.getAuthenticatedUser())
                ) {
                  this.router.navigateByUrl(this.returnRoute);
                } else if (
                  !UserHelper.isClient(this.authService.getAuthenticatedUser())
                ) {
                  this.router.navigateByUrl('/projecten');
                } else {
                  this.router.navigateByUrl('/client-corner/lopende');
                }
              }
            }
            this.saving = false;
          },
          (err) => {
            this.handleSaveError(err);
            this.saving = false;
          }
        );
      }
    }
  }

  private handleSaveError(error) {
    let errMsg =
      'Sorry, er is iets misgegaan, probeer later opnieuw. (Statuscode: ' +
      error.status +
      ')';
    if (error.error) {
      switch (error.error.message) {
        case 'user_exists':
          errMsg =
            'Er bestaat reeds een andere gebruiker met dit e-mailadres. Gelieve een ander e-mailadres te kiezen.';
          break;
        case 'invalid_owner':
          errMsg = 'Je hebt geen rechten om dit object te bewerken';
          break;
      }
    }

    this.snackService.showError(errMsg);
    return throwError(null);
  }

  userIsAdmin(roles: Role[]): boolean {
    for (let role of roles) {
      if (role.name.toString() === 'ROLE_ADMIN') {
        return true;
      }
    }
    return false;
  }

  async fileAdded(fileList) {
    let assets: File[] = Array.from(fileList);
    if (
      CollectionHelper.isPopulated(assets) &&
      assets.some((a) => a.size > MAX_UPLOAD_SIZE)
    ) {
      this.snackService.showError(
        'Gelieve enkel bestanden kleiner dan 12 MB te selecteren'
      );
      return;
    }
    let file = assets[0];
    try {
      let base64 = await AssetHelper.extractBase64(file);
      if (this.user.id == null) {
        this.profilePictureNewUser(file, base64);
      } else {
        this.profilePictureExistingUser(file, base64);
      }
    } catch (e) {
      console.error(e);
      this.snackService.showError(
        'Er is iets mis gegaan bij het opslaan van de bestanden'
      );
      return;
    }
  }

  async profilePictureNewUser(file, base64) {
    let savedAsset = await this.assetHelper.uploadFile(file, base64);
    this.user.profilePicture = savedAsset;
    let successMessage;
    if (savedAsset) {
      successMessage = 'Bestanden succesvol opgeslagen';
    } else {
      successMessage = 'Bestand succesvol opgeslagen';
    }
    this.snackService.showSuccess(successMessage);
  }

  async profilePictureExistingUser(file, base64) {
    let editedUser = await this.changeProfilePicture(file, base64);
    this.user.profilePicture = editedUser.profilePicture;
    let authedUser = this.authService.getAuthenticatedUser();
    if (authedUser != null && this.user.id === authedUser.id) {
      this.authService.getAndSetCurrentUser();
    }
    this.snackService.showSuccess('Bestand succesvol opgeslagen');
  }

  async changeProfilePicture(file: File, base64: string): Promise<User> {
    let asset: Asset = {
      name: file.name,
      type: file.type,
      size: file.size,
      base64: base64,
    };
    return await this.requestService
      .post(asset, 'users/changeProfilePicture/' + this.user.id)
      .toPromise();
  }

  resendLoginEmail() {
    this.modalService.confirmDialog(
      'Login mail opnieuw versturen',
      'Bent u zeker dat u opnieuw een login mail wil versturen naar deze gebruiker?',
      (b) => {
        if (b) {
          this.addSubscription(
            this.requestService.post(
              {
                email: this.user.email,
                resend: true,
              },
              '/auth/recoverAccount'
            ),
            () => {
              this.snackService.showSuccess('Login mail opnieuw verstuurd');
            }
          );
        }
      }
    );
  }

  getLettres() {
    return UserHelper.getLettres(this.user);
  }

  displayFormTitle() {
    return this.currentUserIsAdmin
      ? 'Bewerk gebruiker: ' +
          StringHelper.join(
            [
              this.user.firstName == null
                ? null
                : this.user.firstName.toString(),
              this.user.lastName == null ? null : this.user.lastName.toString(),
            ],
            ' '
          )
      : 'Profiel aanpassen';
  }
}
